
import { createAction } from 'redux-starter-kit';
import { requestStart, requestFailed, requestSucceded } from '../../domain/requests/RequestActions';
import { get, post } from '../../../utility/via-fetch';
import { putCreditPackages } from '../../domain/credits/creditPackageActions';
import { setSessionUserTokens } from '../application/sessionUiActions';
import { putPurchaseHistory } from '../../domain/purchases/purchaseHistoryActions';
import { selectPurchaseHistory } from '../../domain/purchases/purchaseHistoryReducer';
import { addMessage, addNotification } from '../application/messageUiActions';
import putAppConfig from '../../domain/appConfig/appConfigActions';
import ViaNetworkError from '../../../utility/error/ViaNetworkError';


/**
 * An action creator that represents credit packages being fetched.
 * @example dispatch(fetchCreditPackages(requestId))
 */
export const fetchCreditPackages = createAction('FETCH_CREDIT_PACKAGES');

/**
 * A thunk action creator for loading all credit packages into the state.
 * @example dispatch(requestCreditPackages())
 */
export function requestCreditPackages() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchCreditPackages.toString()));
    dispatch(fetchCreditPackages(requestId));

    try {
      const [creditPackages, environment] = await Promise.all([
        get('/api/tokens/packages', { 'active-only': true }),
        get('/api/tokens/purchase/braintree-environment'),
      ]);

      dispatch(putCreditPackages(creditPackages));
      dispatch(putAppConfig({ applicationConfigCode: 'braintreeEnvironment', environment })); // Update the domain state with our newly loaded user.

      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action creator that represents a braintree token being retrieved.
 * @example dispatch(fetchBraintreeToken(requestId))
 */
export const fetchBraintreeToken = createAction('FETCH_BRAINTREE_TOKEN');

/**
 * A thunk action creator for loading the braintree token.
 * @example dispatch(requestBraintreeToken())
 */
export function requestBraintreeToken() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchBraintreeToken.toString()));
    dispatch(fetchBraintreeToken(requestId));

    try {
      const loadedToken = await get('/api/tokens/purchase/braintree-client-token');
      dispatch(requestSucceded(requestId, loadedToken));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}


/**
 * An action creator that represents a credit package being selected.
 * @example dispatch(setChosenCreditPackage(creditPackageId))
 */
export const setChosenCreditPackage = createAction('SET_CHOSEN_CREDIT_PACKAGE');


/**
 * An action creator that represents a credit package being purchased.
 * @example dispatch(fetchPurchasePackage(requestId))
 */
export const fetchPurchasePackage = createAction('FETCH_PURCHASE_PACKAGE');

/**
 * A think action creator for making a request to purchase a credit package.
 * @example dispatch(requestPackagePurchase(braintreeNonce, tokenPackageCode))
 */
export function requestPackagePurchase(nonce, tokenPackageCode) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart());
    dispatch(fetchPurchasePackage(requestId));

    try {
      const result = await post('/api/tokens/purchase', null, { nonce, tokenPackageCode });
      dispatch(requestSucceded(requestId, result));
      dispatch(setSessionUserTokens(result.tokensAvailable));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action creator that represents retrieving the purchase history for the current users group
 */
export const fetchPurchaseHistory = createAction('FETCH_PURCHASE_HISTORY');

/**
 * A thunk action creator for making a request to retrieve the purchase history for the current users group
 * @example dispatch(requestPackagePurchase(braintreeNonce, tokenPackageCode))
 */
export function requestPurchaseHistory() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchPurchaseHistory.toString()));
    dispatch(fetchPurchaseHistory(requestId));

    try {
      const result = await get('/api/tokens/purchase', null, {});
      dispatch(putPurchaseHistory(result));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action creator that represents retrieving the purchase history for the current users group
 */
export const fetchPurchaseHistoryAsAdmin = createAction('FETCH_OTHERS_PURCHASE_HISTORY');

/**
 * A thunk action creator for making a request to retrieve the purchase history for the current users group
 * @example dispatch(requestPurchaseHistoryAsAdmin(groupId))
 */
export function requestPurchaseHistoryAsAdmin(groupId) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchPurchaseHistoryAsAdmin.toString()));
    dispatch(fetchPurchaseHistoryAsAdmin(requestId));

    try {
      const result = await get(`/api/user-groups/${groupId}/purchase-history`, null, {});
      dispatch(putPurchaseHistory(result));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action creator that represents retrieving the purchase history for the current users group
 */
export const fetchUnconfirmedPurchases = createAction('FETCH_UNCONFIRMED_PURCHASES');

/**
 * A thunk action creator for making a request to retrieve the purchase history for the current users group
 * @example dispatch(requestPurchaseHistoryAsAdmin(groupId))
 */
export function requestUnconfirmedPurchases() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchUnconfirmedPurchases.toString()));
    dispatch(fetchUnconfirmedPurchases(requestId));

    try {
      const result = await get('/api/tokens/purchase/unconfirmed', null, {});
      dispatch(putPurchaseHistory(result));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}


/**
 * An action creator that represents searching for a purchase on its unique id
 */
export const searchPurchases = createAction('SEARCH_PURCHASES');

/**
 * A thunk action creator that represents searching for a purchase on its unique id
 * saaves teh data in ath action payload as it is doubtful to be more than one or two records
 * @example dispatch(searchForTokenPurchase(searchStr))
 */
export function searchForTokenPurchase(searchStr) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(searchPurchases.toString()));
    dispatch(searchPurchases(requestId));

    try {
      const result = await get(`/api/tokens/purchase/${searchStr}`, null, {});
      dispatch(requestSucceded(requestId, result));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

export const fetchUpdatePurchase = createAction('FETCH_UPDATE_PURCHASE');

export function requestUpdatePurchase(id, commission, braintreeId, userGroupId) {
  return async (dispatch, getState) => {
    const requestId = dispatch(requestStart(fetchUpdatePurchase.toString()));
    dispatch(fetchUpdatePurchase(requestId));

    try {
      await post(`/api/tokens/purchase/${id}`, null, { commission, braintreeId, userGroupId });

      const purchaseHistories = selectPurchaseHistory(getState()).filter(p => p.code !== id);
      dispatch(putPurchaseHistory([...purchaseHistories]));

      dispatch(addMessage('success', 'itemUpdated', { item: id }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}


export const fetchFailPurchase = createAction('FETCH_FAIL_PURCHASE');

export function requestFailPurchase(id) {
  return async (dispatch, getState) => {
    const requestId = dispatch(requestStart(fetchFailPurchase.toString()));
    dispatch(fetchFailPurchase(requestId));

    try {
      await post(`/api/tokens/purchase/${id}/fail`);

      const purchaseHistories = selectPurchaseHistory(getState()).filter(p => p.code !== id);
      dispatch(putPurchaseHistory([...purchaseHistories]));

      dispatch(addMessage('success', 'itemUpdated', { item: id }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

export const fetchAddFreeCredits = createAction('FETCH_ADD_FREE_CREDITS');

export function requestFetchFreeCredits(username, description, tokens, endDate) {
  return async (dispatch, getState) => {
    const requestId = dispatch(requestStart(fetchAddFreeCredits.toString()));
    dispatch(fetchFailPurchase(requestId));

    try {
      const purchase = await post('/api/tokens/purchase/assign-free', {}, {
        username,
        packageDescription: description,
        extraTokenCount: tokens,
        endDate,
      });

      const purchaseHistories = selectPurchaseHistory(getState());
      dispatch(putPurchaseHistory([...purchaseHistories, purchase]));

      dispatch(addMessage('success', 'itemSaved', { item: purchase.code }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action creator that represents viewing (and generating the invoice)
 */
export const viewAndGenerateInvoice = createAction('VIEW_AND_GENERATE_INVOICE');

/**
 * A thunk action creator that represents viewing (and generating the invoice)
 * @example dispatch(requestPackagePurchase(braintreeNonce, tokenPackageCode))
 */
export function requestViewInvoice(purchaseId, pdfFunction, groupId, generate) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(viewAndGenerateInvoice.toString()));
    dispatch(viewAndGenerateInvoice(requestId));
    try {
      const invoice = await get(`/api/tokens/purchase/invoice/${purchaseId}`, { groupId, generate });
      pdfFunction(invoice);
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
      // Not Found 404
      if (error && error instanceof ViaNetworkError && error.code === 404) {
        dispatch(addNotification('warning', 0, 'viewInvoice', undefined, 'viewInvoiceNotFound'));
      }
    }
  };
}
